import { useState } from 'react';
import {
  createLocalStorage,
  LoginComponent,
  type STEPS,
  type UserSessionData
} from '@cencosud-cencommerce/paris-login';

import { useAppSelector } from '@/Application/Hooks/Redux';
import {
  isAcceptedPointsTermsAndConditionsEnabled,
  isDniRegisteredFeatureEnabled
} from '@/config/featureFlags';
import environment from '@/modules/cart/environments/environment.ts';
import CartReduxRepository from '@/modules/cart/infrastructure/CartReduxRepository/CartReduxRepository.ts';
import { isWeddingFlow } from '@/modules/shared/utils/isWeddingFlow';
import CustomerDataForm from '@/sections/customerData/components/CustomerDataForm.tsx';
import GiftCardInformation from '@/sections/customerData/components/GiftCardInformation.tsx';
import useHandleLogin from '@/sections/customerData/hooks/mutators/useCreateShippingAddressFotGuest/useLogInUserInCheckout.ts';

const { loginEnv } = environment;

type UserSessionShipping = UserSessionData & {
  shippingAddress?: string;
};

const voidUserData: UserSessionShipping = {
  email: '',
  firstName: ''
};

const isWedding = isWeddingFlow();

const userSessionData = createLocalStorage<UserSessionData>('sessionData');

const CustomerDataMain = () => {
  const cart = useAppSelector((state) => state.cart);
  const isDniEnabled = isDniRegisteredFeatureEnabled();
  const isPointsTermsAndConditionsEnabled =
    isAcceptedPointsTermsAndConditionsEnabled();

  const { handleLogin } = useHandleLogin();

  const [isOpenOrStep, setIsOpenOrStep] = useState<STEPS | boolean>(false);
  const [user, setUser] = useState(
    (userSessionData.get() ?? voidUserData) as UserSessionShipping
  );

  const updateUserData = async (userData: UserSessionShipping) => {
    setUser({
      email: userData.email || '',
      firstName: userData.firstName,
      regionId: userData.regionId,
      commune: userData.commune,
      communeName: userData.communeName,
      shippingAddress: userData.shippingAddress || ''
    });
  };

  const handleLoginComponent = async () => {
    const sessionData = userSessionData.get();
    const isUserLoggedIn = sessionData?.email && sessionData?.firstName;
    const isWedding = isWeddingFlow();

    if (isUserLoggedIn) {
      try {
        const anonymousId = cart?.data?.anonymousId || '';
        if (anonymousId && !isWedding) {
          await CartReduxRepository.mergeCart(anonymousId);
        }

        await updateUserData(sessionData);
        setIsOpenOrStep(false);

        await handleLogin();
      } catch (error) {
        console.error('Error al fusionar el carrito:', error);
      }
    } else {
      setIsOpenOrStep(false);
    }
  };

  return (
    <section
      className="co-mb-[168px] co-flex co-flex-col co-gap-6 co-bg-white co-rounded-[8px] co-py-[12px] co-px-[10px] co-mx-[12px] tablet_w:co-mx-[16px] tablet_w:co-px-20 tablet_w:co-mb-[32px] tablet_w:co-py-[56px] co-border co-border-neutral-200"
      data-testid="customer-data-main"
    >
      {!isWedding ? (
        <article className="co-bg-primary-100 co-py-[18px] co-px-[16px] co-rounded-[8px]">
          <GiftCardInformation setIsOpenOrStep={setIsOpenOrStep} />
          <LoginComponent
            isOpenOrStep={isOpenOrStep}
            values={user}
            environment={loginEnv()}
            onClose={handleLoginComponent}
            isEnabledDni={isDniEnabled}
            acceptedPointsTermsAndConditionsEnabled={
              isPointsTermsAndConditionsEnabled
            }
            successfulSignUpBehavior="none"
          />
        </article>
      ) : null}

      <CustomerDataForm />
    </section>
  );
};

export default CustomerDataMain;
